/**
 * Pointer-move behaviour: refine the ghost under the pointer and write the HUD.
 *
 * Every tool follows the same shape — find what is under the pointer, update
 * its preview, describe what a click would do.
 */

import { Vector3 } from "three";
import { ALP_LENGTHS, bracketName, CFG, KR, MM, nodeY, TOE_LENGTHS } from "../../data/haki.ts";
import type { ScaffoldGroup } from "../../model/connectivity.ts";
import {
  bayFrame,
  dist,
  kXZ,
  nearestEdgeInfo,
  nearestMod,
  nearestOf,
  orientCorners,
} from "../../model/geometry.ts";
import { boardLength, findRail, findToe } from "../../model/operations.ts";
import { legHeight, packPlanks, snapXZ } from "../../model/solver.ts";
import { isMounted } from "../../model/types.ts";
import { M } from "../materials.ts";
import type { PlaneGizmoMode } from "../planes.ts";
import { V } from "../threeHelpers.ts";
import {
  clearAccessPreview,
  showHatchPreview,
  showLadderPreview,
  showSmallStairPreview,
  showUtvPreview,
} from "./accessPreview.ts";
import { aimEnd } from "./aim.ts";
import { BEAM_KIND_LABEL, describePart, railSideLabel, railToggleable, rightClickHint } from "./describe.ts";
import { showBoardGhost, showSpirGhost } from "./ghosts.ts";
import {
  chooseBoardSpan,
  formatLevel,
  outwardFromEdge,
  planBoard,
  planFreeEnd,
  resolveUtvPlacement,
  stackTarget,
} from "./shared.ts";
import type { ToolContext } from "./toolContext.ts";

/** Top of a leg, metres — where a stacked standard would start. */
function legTopY(ctx: ToolContext, key: string): number {
  const leg = ctx.model.legs.get(key);
  if (!leg) {
    return 0;
  }

  return nodeY(leg.baseP ?? 0) + legHeight(leg);
}

function hoverDelete(ctx: ToolContext, event: PointerEvent): void {
  const hit = ctx.pickElem(event);
  if (!hit) {
    ctx.clearHot();
    if (ctx.settings.tool === "del") {
      ctx.hideHud();
    }
    return;
  }

  const hint = ctx.isRightPressed() ? rightClickHint(ctx.model, hit.ref) : null;
  if (hint) {
    ctx.clearHot();
    ctx.hud(hint, true);
    return;
  }

  ctx.setHot(hit.ref);
  ctx.hud(`Delete: ${describePart(ctx.model, hit.ref)}`, true);
}

function hoverBeamAim(ctx: ToolContext, event: PointerEvent): boolean {
  const pending = ctx.pending;
  const aim = ctx.ghosts.aimGhost;
  if (pending?.kind !== "beam" || !aim) {
    return false;
  }

  const y = nodeY(pending.p) + KR;
  const plane = ctx.planeAt(event, y);
  if (!plane) {
    aim.visible = false;
    return true;
  }

  const result = aimEnd({
    model: ctx.model,
    from: pending.leg,
    y,
    planePoint: plane,
    hoveredLeg: ctx.hoveredLeg(event),
    selectedSize: ctx.settings.size,
  });

  const from = V(pending.leg.x, y, pending.leg.z);
  const to = V(result.end.x, y, result.end.z);
  const delta = new Vector3().subVectors(to, from);

  aim.visible = true;
  aim.scale.y = delta.length();
  aim.position.copy(from).addScaledVector(delta, 0.5);
  aim.quaternion.setFromUnitVectors(V(0, 1, 0), delta.clone().normalize());

  const duplicate = ctx.model.beams.some(
    (b) => b.p === pending.p && dist(b.a, pending.leg) < 0.02 && dist(b.b, result.end) < 0.02,
  );
  aim.material = duplicate || !result.fits ? M.ghostBad : M.ghost;

  if (!result.fits) {
    ctx.hud(`Distance ${Math.round(dist(pending.leg, result.end) * 1000)} mm fits no module`, true);
    return true;
  }

  const kind = ctx.settings.beamKind === "lb" ? "LB " : "ERB ";
  const adjusted = result.adjusted ? ` (adjusted from ${ctx.settings.size})` : "";
  const target = result.target
    ? "→ standard"
    : `→ free end${ctx.settings.autoSpir ? " (auto-standard)" : ""}`;
  ctx.hud(`${kind}${result.len}${adjusted} · ${target} · click to place`, true);

  return true;
}

function hoverStandard(ctx: ToolContext, event: PointerEvent): void {
  const length = ctx.settings.size * MM;

  const target = stackTarget(ctx, event);
  if (target) {
    showSpirGhost(ctx.ghosts, target.x, target.z, legTopY(ctx, kXZ(target.x, target.z)), length);
    const total = (legHeight(target) + length).toLocaleString("en", { minimumFractionDigits: 1 });
    const where = isMounted(target) && target.segments.length === 0 ? "on the bracket's spigot" : "on top";
    ctx.hud(`Click: Standard S4 AL ${ctx.settings.size} ${where} → ${total} m`, true);
    return;
  }

  const ghost = ctx.pickGhost(event);
  if (ghost && ghost.payload.kind === "legAt") {
    const { x, z, p } = ghost.payload;
    const plan = planFreeEnd(ctx, x, z, p);
    const total = plan.segs.reduce((sum, mm) => sum + mm, 0) * MM;
    showSpirGhost(ctx.ghosts, x, z, plan.gy + 0.02, total);
    ctx.hud(`Click: standard at the free beam end (${plan.segs.join("+")})`, true);
    return;
  }

  const surface = ctx.pickCollider(event);
  if (!surface) {
    if (ctx.ghosts.spirGhost) {
      ctx.ghosts.spirGhost.visible = false;
    }
    ctx.hideHud();
    return;
  }

  const point = ctx.terrain.surfacePoint(surface);
  const snapped = snapXZ(ctx.model, point.x, point.z, ctx.settings.snap);
  const gy = ctx.terrain.resolveGy(snapped.x, snapped.z, point.gy);
  showSpirGhost(ctx.ghosts, snapped.x, snapped.z, gy + 0.02, length);

  const onSurface = gy > 0.05 ? ` · surface ${gy.toFixed(2)} m` : "";
  const onWall = point.onWall ? " · 250 mm from wall" : "";
  const moved = Math.abs(snapped.x - point.x) > 0.01 || Math.abs(snapped.z - point.z) > 0.01;
  const snapNote = ctx.settings.snap && moved ? " (snapped)" : "";
  ctx.hud(
    `Click: Standard S4 AL ${ctx.settings.size} at ${snapped.x.toFixed(2)}, ${snapped.z.toFixed(2)}${onSurface}${onWall}${snapNote}`,
    true,
  );
}

/** True when a bay ghost was hit, so the plank hover below is skipped. */
function hoverDeck(ctx: ToolContext, event: PointerEvent): boolean {
  const ghost = ctx.pickGhost(event);
  if (ghost?.payload.kind !== "deck") {
    return false;
  }

  // Planks rest on the edge nearest the pointer and span to the one opposite.
  const info = nearestEdgeInfo(ghost.payload.bay.c, ghost.point);
  const alongAC = !info.alongAC;
  ghost.payload.alongAC = alongAC;
  ghost.payload.stripes.rotation.y = alongAC ? Math.PI / 2 : 0;

  const frame = bayFrame(orientCorners(ghost.payload.bay.c, alongAC));
  const plankLength = nearestOf(ALP_LENGTHS, frame.L * 1000);
  const count = packPlanks(Math.round(frame.W * 1000), ctx.settings.plankFam).length;
  ctx.hud(`Click: plank ${plankLength} spanning from the nearest beam · ${count} planks`, true);

  return true;
}

/** Single-board mode: the board follows the pointer across the span it is over. */
function hoverBoard(ctx: ToolContext, event: PointerEvent): void {
  const ghost = ctx.pickGhost(event);
  if (ghost?.payload.kind === "board") {
    const { wid } = ghost.payload;
    const span = chooseBoardSpan(ctx.model, wid, ghost.payload.span.p, ghost.point) ?? ghost.payload.span;
    const place = planBoard(ctx.model, span, wid, ghost.point);
    ghost.payload.place = place;
    showBoardGhost(ctx.ghosts, place);
    const name = `AL plank ALP ${wid}×${boardLength(place.board)}`;
    ctx.hud(
      place.blocked
        ? `${name} — this slot is already covered`
        : `Click: ${name} spanning from the nearest beam, level ${formatLevel(span.p)} m`,
      true,
    );
    return;
  }

  showBoardGhost(ctx.ghosts, null);
  const hit = ctx.pickElem(event, (ref) => ref.type === "board");
  if (hit) {
    ctx.setHot(hit.ref);
    ctx.hud(`Click: remove ${describePart(ctx.model, hit.ref)}`, true);
    return;
  }

  ctx.clearHot();
}

function hoverRail(ctx: ToolContext, event: PointerEvent): void {
  const ghost = ctx.pickGhost(event);
  if (ghost && ghost.payload.kind === "rail") {
    const beam = ghost.payload.beam;
    const mod = nearestMod(dist(beam.a, beam.b) * 1000);
    const parts: string[] = [];

    if (ctx.settings.railKind !== "toe" && !findRail(ctx.model, beam.a, beam.b, beam.p)) {
      parts.push(`SKRD ${mod} AL`);
    }
    if (ctx.settings.railKind !== "skrd" && !findToe(ctx.model, beam.a, beam.b, beam.p)) {
      parts.push(`Toe board AL ${nearestOf(TOE_LENGTHS, mod)}`);
    }
    ctx.hud(`Click: ${parts.join(" + ")} on this edge, level ${formatLevel(beam.p)} m`, true);
    return;
  }

  const hit = ctx.pickElem(event, (ref) => ref.type === "rail" || ref.type === "toe");
  if (hit) {
    ctx.setHot(hit.ref);
    ctx.hud(`Click: remove ${describePart(ctx.model, hit.ref)}`, true);
    return;
  }

  ctx.clearHot();
}

function hoverBracket(ctx: ToolContext, event: PointerEvent): void {
  const ghost = ctx.pickGhost(event);
  if (ghost && ghost.payload.kind === "bracket") {
    const { bracketKind, len, p } = ghost.payload;
    const arm = bracketKind === "skd" ? ` + ${BEAM_KIND_LABEL[ctx.settings.beamKind]} ${len}` : "";
    const brace =
      bracketKind === "skd" ? ", braced from the cup group 1.5 m below" : ", cup group + spigot at the end";
    ctx.hud(`Click: ${bracketName(bracketKind, len)}${arm} at level ${formatLevel(p)} m${brace}`, true);
    return;
  }

  const hit = ctx.pickElem(event, (ref) => ref.type === "bracket");
  if (hit) {
    ctx.setHot(hit.ref);
    ctx.hud(`Click: remove ${describePart(ctx.model, hit.ref)}`, true);
    return;
  }

  ctx.clearHot();
}

const GIZMO_VERB: Readonly<Record<PlaneGizmoMode, string>> = {
  move: "move it — the green arrow is its height",
  resize: "resize it — the opposite corner stays put",
  rotate: "rotate it",
};

function hoverPlane(ctx: ToolContext, event: PointerEvent): void {
  const selected = ctx.planes.selectedId;
  const selectedName = selected === null ? null : (ctx.planes.planeById(selected)?.name ?? "plane");

  if (ctx.isPointerOnGizmo() && selectedName !== null) {
    ctx.hud(`Drag to ${GIZMO_VERB[ctx.planes.mode]}: ${selectedName}`, true);
    return;
  }

  const id = ctx.pickPlane(event);
  const name = id === null ? null : (ctx.planes.planeById(id)?.name ?? "plane");
  if (name !== null && id !== selected) {
    ctx.hud(`Click: select ${name}`, true);
    return;
  }
  if (name !== null) {
    ctx.hud(`${name} selected — drag the gizmo, or edit it in the Planes panel`, true);
    return;
  }

  ctx.hud(
    selectedName === null
      ? "Click a plane to select it — Add plane is in Options and in the Planes panel"
      : `Click empty space to deselect ${selectedName}`,
    true,
  );
}

function hoverAccess(ctx: ToolContext, event: PointerEvent): void {
  const host = ctx.ghosts.accessGhost;
  const ghost = ctx.pickGhost(event);

  if (!ghost) {
    clearAccessPreview(host);
    const hit = ctx.pickElem(event, (ref) => ref.type === "stair");
    const stair = hit ? railToggleable(ctx.model, hit.ref) : null;
    if (stair) {
      ctx.hud(`Click: move the handrail — now ${railSideLabel(stair.rail)}`, true);
    }
    return;
  }

  if (!host) {
    return;
  }

  if (ghost.payload.kind === "luke") {
    const deck = ctx.model.decks[ghost.payload.deckIdx];
    if (!deck) {
      return;
    }
    if (deck.hatch) {
      clearAccessPreview(host);
      ctx.hud("Click: remove hatch deck + ladder from this deck", true);
      return;
    }

    const info = nearestEdgeInfo(deck.c, ghost.point);
    showHatchPreview(host, { deck, side: info.side, end: info.end });
    ctx.hud(
      `Click: Hatch deck AL 690×${deck.len} + Ladder ST 2100 — hatch at the pointer corner, ladder leans to the opposite side`,
      true,
    );
    return;
  }

  if (ghost.payload.kind === "utv") {
    const placement = resolveUtvPlacement(ghost.payload.spans, ghost.point);
    if (!placement) {
      return;
    }

    ghost.payload.place = placement;
    showUtvPreview(host, placement);
    const flip = ghost.payload.spans.length > 1 ? " · point at the other end to flip direction" : "";
    ctx.hud(
      `Click: UTV stair ${placement.len}×2000 AL — bottom at the pointer end (${formatLevel(placement.p)} m), rises to +2.0 m${flip}`,
      true,
    );
    return;
  }

  if (ghost.payload.kind === "ladder") {
    const { beam } = ghost.payload;
    const out = outwardFromEdge(beam.a, beam.b, ghost.point);
    showLadderPreview(host, { a: beam.a, b: beam.b, p: beam.p, ...out }, (x, z) => ctx.terrain.groundY(x, z));
    ctx.hud("Click: Ladder ST 2100 hooked on this edge, leaning to the pointer's side", true);
    return;
  }

  if (ghost.payload.kind === "utvs") {
    const { beam, rise } = ghost.payload;
    const out = outwardFromEdge(beam.a, beam.b, ghost.point);
    showSmallStairPreview(host, { a: beam.a, b: beam.b, p: beam.p, ...out, rise });
    const rail = CFG.fzv[rise] ? " + Fzv handrail" : "";
    ctx.hud(`Click: UTV stair ${rise} AL hooked on this edge, descending toward the pointer${rail}`, true);
  }
}

function hoverPlank(ctx: ToolContext, event: PointerEvent): void {
  const hit = ctx.pickPlank(event);
  if (!hit) {
    return;
  }

  const deck = ctx.model.decks[hit.idx];
  if (!deck) {
    return;
  }

  const state = deck.removed.has(hit.slot) ? " — removed, click to put back" : " — click to remove";
  ctx.hud(`ALP ${hit.fam}×${deck.len}${state}`);
}

/** Is `group` the selected scaffold? Groups are rebuilt often, so compare by standards. */
function isSelected(selected: ScaffoldGroup | null, group: ScaffoldGroup): boolean {
  return (
    selected !== null &&
    group.legKeys.size === selected.legKeys.size &&
    [...group.legKeys].every((key) => selected.legKeys.has(key))
  );
}

/** Move tool: light up the scaffold the pointer is over; the selected one keeps its gizmo. */
function hoverMove(ctx: ToolContext, event: PointerEvent): void {
  const selected = ctx.selectedScaffold();
  if (selected && ctx.isPointerOnGizmo()) {
    ctx.highlightGroup(null);
    ctx.hud("Drag the arrows to move the scaffold — it snaps to the standards standing still", true);
    return;
  }

  const group = ctx.groupUnder(event);
  ctx.highlightGroup(group);
  if (!group) {
    ctx.hud(selected ? "Click empty space to deselect" : "Click a scaffold to select it", true);
    return;
  }
  if (isSelected(selected, group)) {
    ctx.hud(`Selected — ${group.legKeys.size} standards · drag the arrows to move it`, true);
    return;
  }

  ctx.hud(`Click: select this scaffold — ${group.legKeys.size} standards`, true);
}

export function handleHover(ctx: ToolContext, event: PointerEvent): void {
  if (ctx.settings.tool === "move" && !ctx.isRightPressed()) {
    hoverMove(ctx, event);
    return;
  }

  const deleting = ctx.settings.tool === "del" || ctx.isRightPressed();

  if (deleting) {
    hoverDelete(ctx, event);
    if (ctx.settings.tool === "del") {
      return;
    }
  } else {
    ctx.clearHot();
  }

  if (hoverBeamAim(ctx, event)) {
    return;
  }

  const { tool } = ctx.settings;
  if (tool === "spir") {
    hoverStandard(ctx, event);
    return;
  }
  if (tool === "deck" && ctx.settings.deckKind === "board") {
    hoverBoard(ctx, event);
    return;
  }
  if (tool === "deck" && hoverDeck(ctx, event)) {
    return;
  }
  if (tool === "rail") {
    hoverRail(ctx, event);
    return;
  }
  if (tool === "bracket") {
    hoverBracket(ctx, event);
    return;
  }
  if (tool === "plane") {
    hoverPlane(ctx, event);
    return;
  }
  if (tool === "access") {
    hoverAccess(ctx, event);
    return;
  }
  if (tool === "nav" || tool === "deck") {
    hoverPlank(ctx, event);
  }
}
